import { Fragment, useEffect, useRef, useState } from 'react';
import { Alert, Modal, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import type { GigWorkerClient } from '../../../infrastructure/GigWorkerClient';
import type { GigOrderAdminDetail, GigOrderReference } from '../../../models/GigWorker';
import GigOrderPageSnapshots from './GigOrderPageSnapshots';
import { GigItemReason, GigItemReasonHistory, GigOrderSkips } from '@/pages/oms/gig-workers/GigOrderReasonDetails';

interface GigOrderDetailModalProps {
  client: GigWorkerClient;
  reference: GigOrderReference | null;
  onHide: () => void;
}

const formatKrw = (value: number): string => `₩${value.toLocaleString()}`;

/** Shared operational-detail modal opened from either the Gig Workers page or an OMS order. */
export default function GigOrderDetailModal({ client, reference, onHide }: GigOrderDetailModalProps) {
  const requestId = useRef(0);
  const [detail, setDetail] = useState<GigOrderAdminDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const orderId = reference?.orderId;
    if (!orderId) {
      requestId.current += 1;
      setDetail(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    const currentRequest = ++requestId.current;
    setDetail(null);
    setError(null);
    setIsLoading(true);
    client
      .getOrderDetail(orderId)
      .then((result) => {
        if (currentRequest === requestId.current) {
          setDetail(result);
        }
      })
      .catch((loadError: unknown) => {
        if (currentRequest === requestId.current) {
          setError(loadError instanceof Error ? loadError.message : 'Failed to load order detail');
        }
      })
      .finally(() => {
        if (currentRequest === requestId.current) {
          setIsLoading(false);
        }
      });
  }, [client, reference?.orderId]);

  const contextOmsOrderRef = reference?.omsOrderRef ?? detail?.omsOrderRef;

  return (
    <Modal show={reference !== null} onHide={onHide} size="lg" scrollable aria-labelledby="gig-order-detail-title">
      <Modal.Header closeButton>
        <Modal.Title id="gig-order-detail-title" className="fs-6">
          GIG Batch{contextOmsOrderRef ? ` — OMS ${contextOmsOrderRef}` : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading && (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" role="status" aria-label="Loading order detail" />
          </div>
        )}
        {error && <Alert variant="danger">{error}</Alert>}
        {detail && !isLoading && (
          <>
            <dl className="row mb-3 small">
              <dt className="col-4 text-muted">GIG batch id</dt>
              <dd className="col-8 font-monospace">{detail.orderId}</dd>
              <dt className="col-4 text-muted">Source OMS orders</dt>
              <dd className="col-8 d-flex flex-wrap gap-2">
                {detail.omsOrderRefs.map((omsOrderRef) => (
                  <Link
                    key={omsOrderRef}
                    to={`/oms/orders/${omsOrderRef}`}
                    className="text-primary text-decoration-none"
                  >
                    {omsOrderRef}
                  </Link>
                ))}
              </dd>
              <dt className="col-4 text-muted">Physical units</dt>
              <dd className="col-8">{detail.unitCount}</dd>
              <dt className="col-4 text-muted">Status</dt>
              <dd className="col-8">{detail.status}</dd>
              <dt className="col-4 text-muted">Reward</dt>
              <dd className="col-8">{formatKrw(detail.rewardKrw)}</dd>
              <dt className="col-4 text-muted">Coupang order #</dt>
              <dd className="col-8">{detail.coupangOrderId ?? '—'}</dd>
              <dt className="col-4 text-muted">Claimed by</dt>
              <dd className="col-8">{detail.claimedByName ?? detail.claimedBy ?? '—'}</dd>
              <dt className="col-4 text-muted">Claimed at</dt>
              <dd className="col-8">{detail.claimedAt ? new Date(detail.claimedAt).toLocaleString() : '—'}</dd>
              <dt className="col-4 text-muted">Deadline</dt>
              <dd className="col-8">{detail.deadlineAt ? new Date(detail.deadlineAt).toLocaleString() : '—'}</dd>
              <dt className="col-4 text-muted">Skip count</dt>
              <dd className="col-8">{detail.skipCount}</dd>
              <dt className="col-4 text-muted">Tracking</dt>
              <dd className="col-8">{detail.trackingNumber ?? '—'}</dd>
            </dl>

            <h6 className="small text-uppercase text-muted">Recipient</h6>
            {detail.recipient && Object.keys(detail.recipient).length > 0 ? (
              <dl className="row mb-3 small">
                {Object.entries(detail.recipient).map(([key, value]) => (
                  <Fragment key={key}>
                    <dt className="col-4 text-muted">{key}</dt>
                    <dd className="col-8">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</dd>
                  </Fragment>
                ))}
              </dl>
            ) : (
              <p className="text-muted small mb-3">No recipient data.</p>
            )}

            <h6 className="small text-uppercase text-muted">Batch items ({detail.items.length})</h6>
            <Table responsive bordered size="sm" className="mb-3 align-middle">
              <thead className="table-light">
                <tr>
                  <th>Item</th>
                  <th>Source OMS</th>
                  <th className="text-end">Allocated</th>
                  <th className="text-end">Worker</th>
                  <th className="text-end">Internal</th>
                  <th>Owner</th>
                  <th>Status</th>
                  <th>Internal PIC</th>
                </tr>
              </thead>
              <tbody>
                {detail.items.map((item, index) => (
                  <Fragment key={item.itemId ?? index}>
                    <tr>
                      <td>
                        <div>{item.name ?? item.itemId ?? '—'}</div>
                        {item.name && item.itemId && (
                          <div className="small text-muted font-monospace">{item.itemId}</div>
                        )}
                      </td>
                      <td>
                        <Link to={`/oms/orders/${item.omsOrderRef}`} className="text-primary text-decoration-none">
                          {item.omsOrderRef}
                        </Link>
                      </td>
                      <td className="text-end">{item.quantity}</td>
                      <td className="text-end">{item.workerQuantity}</td>
                      <td className="text-end">{item.internalQuantity}</td>
                      <td>{item.purchaseOwner}</td>
                      <td>{item.purchaseStatus}</td>
                      <td>{item.internalPic ?? '—'}</td>
                    </tr>
                    <tr aria-label={`Item reason ${item.itemId ?? index}`}>
                      <td colSpan={8} className="small bg-light">
                        <GigItemReason item={item} />
                      </td>
                    </tr>
                  </Fragment>
                ))}
                {detail.items.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-2">
                      No items.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            <GigOrderSkips detail={detail} />

            <h6 className="small text-uppercase text-muted">Shipments ({detail.shipments.length})</h6>
            <Table bordered size="sm" className="mb-3 align-middle">
              <thead className="table-light">
                <tr>
                  <th>Carrier</th>
                  <th>Tracking</th>
                  <th>Source allocations</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {detail.shipments.map((shipment) => (
                  <tr key={shipment.shipmentId}>
                    <td>{shipment.carrier}</td>
                    <td className="font-monospace">{shipment.trackingNumber}</td>
                    <td>
                      {shipment.items.length > 0
                        ? shipment.items.map((item) => (
                            <div
                              key={`${item.itemId}-${item.omsOrderRef}`}
                            >{`${item.itemId} · OMS ${item.omsOrderRef} · ${item.quantity} units`}</div>
                          ))
                        : '—'}
                    </td>
                    <td>{new Date(shipment.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
                {detail.shipments.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-2">
                      No shipments submitted.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            <h6 className="small text-uppercase text-muted">Timeline ({detail.events.length})</h6>
            <Table bordered size="sm" className="mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th>When</th>
                  <th>Event</th>
                  <th>Actor</th>
                </tr>
              </thead>
              <tbody>
                {detail.events.map((event, index) => (
                  <tr key={`${event.createdAt}-${index}`}>
                    <td className="text-nowrap">{new Date(event.createdAt).toLocaleString()}</td>
                    <td>
                      {event.eventType}
                      <GigItemReasonHistory event={event} items={detail.items} />
                    </td>
                    <td className="small text-muted">
                      {event.actorType}
                      {event.actorId ? ` (${event.actorId})` : ''}
                    </td>
                  </tr>
                ))}
                {detail.events.length === 0 && (
                  <tr>
                    <td colSpan={3} className="text-center text-muted py-2">
                      No events.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            <h6 className="small text-uppercase text-muted mt-3">Page replay</h6>
            <GigOrderPageSnapshots client={client} orderId={detail.orderId} />
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}
